/**
 * ResultSeriesSync
 * Two stacked Highcharts Stock charts that share a crosshair and tooltip:
 *   Top    – Demand (Load / BESS / PV / Genset / Grid + Battery SoC on right axis)
 *   Bottom – Energy Cost (Import Tariff + Tariff Effective) in $/kWh
 *
 * Hover on either chart → both show a synced crosshair + tooltip for the same timestamp.
 * Brushing the navigator (bottom of the cost chart) zooms both charts together.
 */
import "./resultSeriesStyle.scss";
import { useMantineColorScheme } from "@mantine/core";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import moment from "moment";
import HighchartsReact from "highcharts-react-official";
import FullscreenModal from "../common/FullscreenModal/FullscreenModal";
import { useTranslation } from "react-i18next";
import { formatEnUsValue, formatProjectDateTime, formatProjectNumber, useCurrency } from "../../utils/helpers";
import {
  fontFamily,
  stepMs,
  SERIES_CONFIG,
  SOC_CONFIG,
  leftIndex,
  rightIndex,
  nearestIndex,
  nearestRenderedPoint,
  toSocPercent,
} from "./chartHelpers";

// ─── Highcharts bootstrap ────────────────────────────────────────────────────
const rootScope = typeof window !== "undefined" ? window : {};
if (rootScope) {
  const cssObj = rootScope.CSS || {};
  if (typeof cssObj.supports !== "function") cssObj.supports = () => false;
  if (!rootScope.CSS) rootScope.CSS = cssObj;
}
const highstockModule = require("highcharts/highstock");
const Highcharts = highstockModule.default || highstockModule;
const boostModule = require("highcharts/modules/boost");
const initBoost = boostModule.default || boostModule;
if (typeof initBoost === "function") initBoost(Highcharts);
// timesMs is built in local time — tell Highcharts to render in local time too
// so chart pixel positions match array indices exactly.
Highcharts.setOptions({ time: { useUTC: false } });

// ─── Constants ───────────────────────────────────────────────────────────────
// SERIES_CONFIG, SOC_CONFIG, fontFamily, stepMs imported from chartHelpers.js

const COST_CONFIG = [
  {
    id: "importTariff",
    labels: ["purchasecost", "purchase_cost", "importtariff", "import_tariff"],
    color: "#f4a23a",
    label: "Energy Charges",
  },
  {
    id: "tariffEffective",
    labels: ["tariffeffective", "tariff_effective", "effectivetariff"],
    color: "#a78bfa",
    label: "Demand Charges",
  },
];

// ─── Thermal series config ────────────────────────────────────────────────────
const THERMAL_BEFORE_CONFIG = { id: "thermalBefore", labels: ["thermalbefore", "thermal_before"], color: "#ff7f50", label: "Energy Required" };
const THERMAL_AFTER_CONFIG  = { id: "thermalAfter",  labels: ["thermalafter",  "thermal_after"],  color: "#ff66aa", label: "Energy Served"  };

// ─── CO2 series config ───────────────────────────────────────────────────────
const CO2_BEFORE_CONFIG = { id: "co2EmissionBefore", labels: ["co2emissionbefore", "co2_emission_before"], color: "#6c757d", label: "Emissions Before" };
const CO2_AFTER_CONFIG  = { id: "co2EmissionAfter",  labels: ["co2emissionafter",  "co2_emission_after"],  color: "#28a745", label: "Emissions After"  };

// Set of IDs we own — used to skip navigator/internal series when applying hover state
const ALL_SERIES_IDS = new Set([
  ...SERIES_CONFIG.map((c) => c.id),
  SOC_CONFIG.id,
  ...COST_CONFIG.map((c) => c.id),
  THERMAL_BEFORE_CONFIG.id,
  THERMAL_AFTER_CONFIG.id,
  CO2_BEFORE_CONFIG.id,
  CO2_AFTER_CONFIG.id,
]);

// ─── Helpers ─────────────────────────────────────────────────────────────────
// leftIndex, rightIndex, nearestIndex, nearestRenderedPoint, toSocPercent imported from chartHelpers.js

const sameDomain = (a, b) => {
  if (!a && !b) return true;
  if (!a || !b) return false;
  return Math.abs(a[0] - b[0]) < 1 && Math.abs(a[1] - b[1]) < 1;
};
const matchKey = (resultsObj, labels) => {
  const r = resultsObj || {};
  const lowerMap = Object.keys(r).reduce((acc, k) => { acc[k.toLowerCase()] = k; return acc; }, {});
  const hit = labels.find((l) => lowerMap[l]);
  if (!hit) return null;
  const orig = lowerMap[hit];
  return Array.isArray(r[orig]) && r[orig].length ? r[orig] : null;
};

function formatDateAxisLabel() {
  const span = (this.axis.max || 0) - (this.axis.min || 0);
  const day = 86400000;
  if (span <= 16 * day) return moment(this.value).format("D MMM YYYY H:mm");
  if (span <= 120 * day) return moment(this.value).format("D MMM YYYY");
  return moment(this.value).format("MMM YYYY");
}

function formatPowerAxisLabel() {
  const value = this.value;
  const abs = Math.abs(value);
  if (abs >= 1e9) return `${(value / 1e9).toFixed(2)}B`;
  if (abs >= 1e6) return `${(value / 1e6).toFixed(2)}M`;
  return formatProjectNumber(value);
}

function formatSocAxisLabel() {
  return `${Math.round(this.value)}%`;
}

function formatCostAxisLabel() {
  return formatEnUsValue(this.value);
}

// ─── Shared chart options builder ────────────────────────────────────────────
const HOVER_STATE = { halo: { size: 0 }, lineWidthPlus: 0, brightness: 0 };
const INACTIVE_STATE = { opacity: 1 };

function buildBase({ bg, axisText, grid, mask, outline, panMode, legendGap, showNavigator, chartHeight, marginRight }) {
  return {
    // seriesThreshold set high so chart-level boost never activates.
    // Chart-level boost empties s.points (WebGL), defeating our snap-to-rendered-point logic.
    // With dataGrouping ON, only ~400-600 grouped points are rendered — SVG handles this fine.
    boost: { seriesThreshold: Number.MAX_VALUE },
    chart: {
      backgroundColor: bg,
      height: chartHeight,
      animation: false,
      spacingTop: Math.max(8, legendGap),
      style: { fontFamily },
      panning: { enabled: panMode, type: "x" },
      zooming: { type: panMode ? undefined : "x", mouseWheel: { enabled: false }, resetButton: { theme: { display: "none" } } },
      ...(marginRight != null ? { marginRight } : {}),
    },
    credits:       { enabled: false },
    title:         { text: null },
    rangeSelector: { enabled: false },
    scrollbar:     { enabled: false },
    legend:        { enabled: false },
    navigator: {
      enabled: showNavigator,
      height: 44,
      margin: 6,
      maskFill: mask,
      outlineColor: outline,
      handles: { backgroundColor: outline, borderColor: outline },
      xAxis: { labels: { enabled: false }, gridLineWidth: 0 },
      yAxis: { gridLineWidth: 0 },
      series: { type: "line", lineWidth: 1 },
    },
    xAxis: {
      type: "datetime",
      lineColor: grid,
      tickColor: grid,
      crosshair: { color: axisText, dashStyle: "Dash", width: 1, snap: false, label: { enabled: false } },
      labels: {
        style: { color: axisText, fontSize: "11px" },
        formatter: formatDateAxisLabel,
      },
    },
    plotOptions: {
      series: {
        animation: false,
        states: { hover: HOVER_STATE, inactive: INACTIVE_STATE },
        turboThreshold: 0,
        boostThreshold: Number.MAX_VALUE,
        dataGrouping: {
          enabled: true,
          groupPixelWidth: 2,
          approximation: "average",
          smoothed: false,
        },
        stickyTracking: false,
        events: {},
      },
    },
    tooltip: {
      enabled: false, // we drive tooltip manually via sync
    },
  };
}

// ─── Component ───────────────────────────────────────────────────────────────
const ResultSeriesSync = ({
  results,
  kpiProjectSettings,
  onFullscreen,
  inModalInstance = false,
  legendGap = 16,
  thermalMode = false, // when true: top=Energy Required, bottom=Energy Served
  co2Mode = false,     // when true: top=CO2 Emissions Before/After
}) => {
  const { t }           = useTranslation();
  const currency        = useCurrency();
  const { colorScheme } = useMantineColorScheme();

  const demandRef = useRef(null);
  const costRef   = useRef(null);

  const [hidden,         setHidden]         = useState(new Set());
  const [xDomain,        setXDomain]        = useState(null); // [ms, ms]
  const [panMode,        setPanMode]        = useState(false);
  const [powerUnit,      setPowerUnit]      = useState("W");
  const [fullscreenModal, setFullscreenModal] = useState(false);
  const hiddenRef = useRef(hidden);
  const hoverRafRef = useRef(null);
  const pendingHoverXRef = useRef(null);

  useEffect(() => {
    hiddenRef.current = hidden;
  }, [hidden]);

  const handleCloseModal = useCallback(() => {
    setFullscreenModal(false);
    if (typeof onFullscreen === "function") onFullscreen();
  }, [onFullscreen]);

  // ── Unit helpers ───────────────────────────────────────────────────────────
  const unitHelpers = useMemo(() => {
    if (co2Mode) {
      const scaleValue = (v) => { const n = +v; return Number.isFinite(n) ? n : null; };
      return { unitFactor: 1, unitLabel: "kg CO\u2082", scaleValue, powerAxisLabel: "CO\u2082 Emissions (kg)" };
    }
    const unitFactor = powerUnit === "kW" ? 1 / 1000 : 1;
    const unitSuffix = thermalMode ? "_th" : "";
    const unitLabel  = powerUnit + unitSuffix;
    const scaleValue = (v) => { const n = +v; return Number.isFinite(n) ? n * unitFactor : null; };
    const powerAxisLabel = powerUnit === "kW" ? t("timeSeries.axis.powerKW") : t("timeSeries.axis.powerW");
    return { unitFactor, unitLabel, scaleValue, powerAxisLabel };
  }, [powerUnit, thermalMode, co2Mode, t]);

  // ── Series detection ───────────────────────────────────────────────────────
  const demandSeries = useMemo(() => {
    const r = results || {};
    const lowerMap = Object.keys(r).reduce((acc, k) => { acc[k.toLowerCase()] = k; return acc; }, {});
    const configs = thermalMode ? [THERMAL_BEFORE_CONFIG, THERMAL_AFTER_CONFIG]
      : co2Mode ? [CO2_BEFORE_CONFIG, CO2_AFTER_CONFIG]
      : SERIES_CONFIG;
    return configs.reduce((acc, cfg) => {
      const hit = cfg.labels.find((l) => lowerMap[l]);
      if (!hit) return acc;
      const raw = r[lowerMap[hit]];
      if (Array.isArray(raw) && raw.length)
        acc.push({ key: cfg.id, label: cfg.label, color: cfg.color, values: raw });
      return acc;
    }, []);
  }, [results, thermalMode, co2Mode]);

  const socData = useMemo(() => {
    if (thermalMode || co2Mode) return null; // no SoC equivalent for thermal/co2
    const raw = matchKey(results, SOC_CONFIG.labels);
    return raw ? { key: SOC_CONFIG.id, label: SOC_CONFIG.label, color: SOC_CONFIG.color, values: raw } : null;
  }, [results, thermalMode, co2Mode]);

  const costSeries = useMemo(() => {
    // Always use tariff series (Import Tariff + Tariff Effective) regardless of thermalMode
    return COST_CONFIG.reduce((acc, cfg) => {
      const raw = matchKey(results, cfg.labels);
      if (raw) acc.push({ key: cfg.id, label: cfg.label, color: cfg.color, values: raw });
      return acc;
    }, []);
  }, [results]);

  // ── Timeline ───────────────────────────────────────────────────────────────
  const maxLen = useMemo(() => {
    const lens = [...demandSeries.map((s) => s.values.length), ...costSeries.map((s) => s.values.length)];
    if (socData) lens.push(socData.values.length);
    return lens.length ? Math.max(...lens) : 0;
  }, [demandSeries, costSeries, socData]);

  const startDate = useMemo(() => {
    if (!maxLen) return null;
    if (kpiProjectSettings?.startYear) return new Date(kpiProjectSettings.startYear, 0, 1, 0, 0, 0, 0);
    const d = new Date(); d.setFullYear(d.getFullYear() - 1); d.setHours(0, 0, 0, 0); return d;
  }, [maxLen, kpiProjectSettings]);

  const timesMs = useMemo(() => {
    if (!maxLen || !startDate) return [];
    const base = startDate.getTime();
    const arr = new Array(maxLen);
    for (let i = 0; i < maxLen; i++) arr[i] = base + i * stepMs;
    return arr;
  }, [maxLen, startDate]);

  // ── Y-range for demand chart ───────────────────────────────────────────────
  const demandYRange = useMemo(() => {
    const { scaleValue } = unitHelpers;
    let lo = Infinity, hi = -Infinity;
    for (const s of demandSeries) {
      for (let i = 0; i < s.values.length; i++) {
        const v = scaleValue(s.values[i]);
        if (!Number.isFinite(v)) continue;
        if (v < lo) lo = v;
        if (v > hi) hi = v;
      }
    }
    if (!Number.isFinite(hi)) { hi = 1; lo = 0; }
    if (lo > 0) lo = 0;
    if (hi === lo) { hi = hi === 0 ? 1 : hi; lo = 0; }
    const span = hi - lo;
    return { min: lo, max: hi + span * 0.002 };
  }, [demandSeries, unitHelpers]);

  // ── Legend stats ───────────────────────────────────────────────────────────
  const windowBounds = useMemo(() => {
    if (!timesMs.length || !xDomain) return { startIdx: 0, endIdx: timesMs.length - 1 };
    const s = Math.max(0, leftIndex(timesMs, xDomain[0]) - 1);
    const e = Math.min(timesMs.length - 1, rightIndex(timesMs, xDomain[1]));
    return { startIdx: s, endIdx: e };
  }, [timesMs, xDomain]);

  const legendItems = useMemo(() => {
    const { scaleValue, unitLabel } = unitHelpers;
    const stat = (values, scaleFn, unit) => {
      let lo = Infinity, hi = -Infinity;
      for (let i = windowBounds.startIdx; i <= windowBounds.endIdx && i < values.length; i++) {
        const v = scaleFn(values[i]);
        if (!Number.isFinite(v)) continue;
        if (v < lo) lo = v;
        if (v > hi) hi = v;
      }
      return {
        minText: Number.isFinite(lo) ? formatEnUsValue(lo) : "-",
        maxText: Number.isFinite(hi) ? formatEnUsValue(hi) : "-",
        unitLabel: unit,
      };
    };
    const items = [
      ...demandSeries.map((s) => ({ ...s, group: "demand", ...stat(s.values, scaleValue, unitLabel) })),
      ...(socData ? [{ ...socData, group: "demand", ...stat(socData.values, toSocPercent, "%") }] : []),
      ...costSeries.map((s) => ({ ...s, group: "cost",
        ...stat(s.values, (v) => +v, currency || "$") })),
    ];
    return items;
  }, [demandSeries, socData, costSeries, unitHelpers, windowBounds, currency]);

  // ── Build Highcharts series arrays ─────────────────────────────────────────
  const isShadingVisible = true;

  const mkSeriesItem = (
    key,
    label,
    color,
    data,
    yAxisIdx,
    navigatorOn,
    withShading,
    groupingApprox = "average",
    groupingEnabled = true,
    seriesBoostThreshold = Number.MAX_VALUE
  ) => ({
    type: withShading ? "area" : "line",
    id: key,
    name: label,
    color,
    lineWidth: 1.4,
    fillColor: withShading ? {
      linearGradient: { x1: 0, x2: 0, y1: 0, y2: 1 },
      stops: [
        [0, Highcharts.color(color).setOpacity(0.28).get("rgba")],
        [1, Highcharts.color(color).setOpacity(0).get("rgba")],
      ],
    } : undefined,
    yAxis: yAxisIdx,
    data,
    visible: true, // visibility applied imperatively via hidden useEffect
    showInNavigator: !!navigatorOn,
    navigatorOptions: { color, lineWidth: 1 },
    boostThreshold: seriesBoostThreshold,
    marker: {
      enabled: false,
      states: { hover: { enabled: true, radius: 3, fillColor: "#eb7857", lineColor: colorScheme === "dark" ? "#fff" : "#222", lineWidth: 1.2 } },
    },
    dataGrouping: {
      enabled: groupingEnabled,
      groupPixelWidth: 2,
      approximation: groupingApprox,
      smoothed: false,
    },
    states: { hover: HOVER_STATE, inactive: INACTIVE_STATE },
  });

  const spanMs = useMemo(() => {
    if (!timesMs.length) return 0;
    return Math.max(0, timesMs[timesMs.length - 1] - timesMs[0]);
  }, [timesMs]);

  const showBucketExtrema = false;

  const buildBucketExtremaPoints = (data, bucketMs = 14 * 24 * 60 * 60 * 1000) => {
    if (!Array.isArray(data) || data.length < 2) return [];
    const buckets = new Map();
    for (let i = 0; i < data.length; i++) {
      const p = data[i];
      const x = Array.isArray(p) ? p[0] : p?.x;
      const y = Array.isArray(p) ? p[1] : p?.y;
      if (!Number.isFinite(x) || !Number.isFinite(y)) continue;
      const b = Math.floor(x / bucketMs);
      const cur = buckets.get(b);
      if (!cur) {
        buckets.set(b, { min: { x, y }, max: { x, y } });
      } else {
        if (y < cur.min.y) cur.min = { x, y };
        if (y > cur.max.y) cur.max = { x, y };
      }
    }
    const pts = [];
    buckets.forEach(({ min, max }) => {
      pts.push({
        x: min.x,
        y: min.y,
        color: "#ff6b6b",
        marker: { enabled: true, radius: 5, symbol: "triangle-down", lineColor: "#ffffff", lineWidth: 1.4 },
      });
      if (max.x !== min.x || max.y !== min.y) {
        pts.push({
          x: max.x,
          y: max.y,
          color: "#2fb344",
          marker: { enabled: true, radius: 5, symbol: "triangle", lineColor: "#ffffff", lineWidth: 1.4 },
        });
      }
    });
    return pts;
  };

  const mkExtremaSeries = (parentId, color, data, yAxisIdx) => ({
    type: "scatter",
    id: `${parentId}__extrema`,
    linkedTo: parentId,
    name: `${parentId} extrema`,
    yAxis: yAxisIdx,
    data,
    color,
    showInLegend: false,
    showInNavigator: false,
    enableMouseTracking: false,
    turboThreshold: 0,
    boostThreshold: Number.MAX_VALUE,
    dataGrouping: { enabled: false },
    marker: { enabled: true, radius: 5, lineColor: "#ffffff", lineWidth: 1.4 },
    zIndex: 20,
    states: { inactive: INACTIVE_STATE },
  });

  const hcDemandSeries = useMemo(() => {
    const { scaleValue } = unitHelpers;
    const items = demandSeries.flatMap((s) => {
      const data = [];
      const len = Math.min(timesMs.length, s.values.length);
      for (let i = 0; i < len; i++) data.push([timesMs[i], scaleValue(s.values[i])]);
      const main = mkSeriesItem(s.key, s.label, s.color, data, 0, true, isShadingVisible);
      if (!showBucketExtrema) return [main];
      const extremaData = buildBucketExtremaPoints(data);
      return [main, mkExtremaSeries(s.key, s.color, extremaData, 0)];
    });
    if (socData) {
      const data = [];
      const len = Math.min(timesMs.length, socData.values.length);
      for (let i = 0; i < len; i++) {
        const v = toSocPercent(socData.values[i]);
        data.push([timesMs[i], Number.isFinite(v) ? v : null]);
      }
      items.push({
        ...mkSeriesItem(
          socData.key,
          socData.label,
          socData.color,
          data,
          1,
          false,
          false,
          "low",
          true,
          Number.MAX_VALUE
        ),
        tooltip: { valueSuffix: " %" },
      });
      if (showBucketExtrema) {
        items.push(mkExtremaSeries(socData.key, socData.color, buildBucketExtremaPoints(data), 1));
      }
    }
    return items;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [demandSeries, socData, timesMs, unitHelpers, colorScheme, isShadingVisible, showBucketExtrema]); // 'hidden' omitted — visibility applied imperatively

  const hcCostSeries = useMemo(() => {
    return costSeries.map((s) => {
      const data = [];
      const len = Math.min(timesMs.length, s.values.length);
      for (let i = 0; i < len; i++) {
        const v = +s.values[i];
        data.push([timesMs[i], Number.isFinite(v) ? v : null]);
      }
      return mkSeriesItem(s.key, s.label, s.color, data, 0, true, isShadingVisible);
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [costSeries, timesMs, colorScheme, isShadingVisible]); // 'hidden' omitted — visibility applied imperatively

  const applyHiddenVisibility = useCallback((hiddenSet, chartArg) => {
    const charts = chartArg ? [chartArg] : [demandRef.current?.chart, costRef.current?.chart].filter(Boolean);
    charts.forEach((chart) => {
      if (!chart?.series?.length) return;
      let changed = false;
      chart.series.forEach((series) => {
        const id = series.options?.id;
        if (!id) return;
        const parentId = id.endsWith("__extrema") ? id.replace(/__extrema$/, "") : id;
        if (!ALL_SERIES_IDS.has(parentId)) return;
        const shouldBeVisible = !hiddenSet.has(parentId);
        if (series.visible !== shouldBeVisible) {
          series.setVisible(shouldBeVisible, false);
          changed = true;
        }
      });
      if (changed) chart.redraw(false);
    });
  }, []);

  // Re-apply visibility after series rebuilds (zoom/unit/mode changes).
  useEffect(() => {
    applyHiddenVisibility(hidden);
  }, [hidden, hcDemandSeries, hcCostSeries, applyHiddenVisibility]);

  const handleLegendToggle = useCallback((key) => {
    setHidden((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      hiddenRef.current = next;
      applyHiddenVisibility(next);
      return next;
    });
  }, [applyHiddenVisibility]);

  // ── Chart options ──────────────────────────────────────────────────────────
  const bg       = colorScheme === "dark" ? "#111111" : "#ffffff";
  const axisText = colorScheme === "dark" ? "#d5d7e0" : "#495057";
  const grid     = colorScheme === "dark" ? "#2f2f2f" : "#e9ecef";
  const mask     = colorScheme === "dark" ? "rgba(80,160,255,0.18)" : "rgba(0,123,255,0.13)";
  const outline  = colorScheme === "dark" ? "#4da3ff" : "#007bff";
  const tooltipBg     = colorScheme === "dark" ? "#222" : "#fff";
  const tooltipBorder = colorScheme === "dark" ? "#555" : "#ccc";
  const tooltipText   = colorScheme === "dark" ? "#d5d7e0" : "#222";

  // Always reserve space for right axis (60px) to keep plot area width consistent when switching layers
  // This prevents chart width from changing when switching between electrical (with SoC), thermal, and co2 modes
  const socRightMargin = 60;
  const baseArgs = { bg, axisText, grid, mask, outline, panMode, legendGap, marginRight: socRightMargin };

  const demandOptions = useMemo(() => {
    const base = buildBase({ ...baseArgs, showNavigator: false, chartHeight: 300 });
    return {
      ...base,
      xAxis: {
        ...base.xAxis,
        events: {
          setExtremes(e) {
            // Programmatic sync call — already handled by Highcharts, skip React state update.
            if (e.trigger === "syncExtremes") return;
            if (e.DOMEvent?.type === "mousemove") {
              // Skip React state update during live drag (would call chart.update() and fight the drag).
              // Still sync the cost chart imperatively so both charts move together.
              if (typeof e.min === "number" && typeof e.max === "number") {
                const other = costRef.current?.chart;
                if (other?.xAxis[0]) other.xAxis[0].setExtremes(e.min, e.max, true, false, { trigger: "syncExtremes" });
              }
              return;
            }
            const next = typeof e.min === "number" && typeof e.max === "number" ? [e.min, e.max] : null;
            if (!sameDomain(xDomain, next)) setXDomain(next);
            // sync other chart on drag-end / zoom
            if (e.trigger !== "syncExtremes") {
              const other = costRef.current?.chart;
              if (other?.xAxis[0]) other.xAxis[0].setExtremes(e.min, e.max, true, false, { trigger: "syncExtremes" });
            }
          },
        },
      },
      yAxis: [
        {
          title: { text: unitHelpers.powerAxisLabel, style: { color: axisText, fontSize: "12px" } },
          min: demandYRange.min,
          max: demandYRange.max,
          startOnTick: false,
          endOnTick: false,
          opposite: false,
          gridLineColor: grid,
          labels: {
            style: { color: axisText, fontSize: "11px" },
            formatter: formatPowerAxisLabel,
          },
          plotLines: demandYRange.min < 0
            ? [{ value: 0, color: colorScheme === "dark" ? "#555" : "#999", dashStyle: "Dash", width: 1, zIndex: 2 }]
            : [],
        },
        ...(socData ? [{
          title: { text: "Battery SoC (%)", style: { color: axisText, fontSize: "12px" }, margin: 5 },
          min: 0,
          max: 100,
          startOnTick: false,
          endOnTick: false,
          opposite: true,
          gridLineWidth: 0,
          labels: {
            style: { color: axisText, fontSize: "11px" },
            formatter: formatSocAxisLabel,
          },
        }] : []),
      ],
      series: hcDemandSeries,
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bg, axisText, grid, panMode, legendGap, unitHelpers, demandYRange, hcDemandSeries, colorScheme]);

  const costOptions = useMemo(() => {
    const base = buildBase({ ...baseArgs, showNavigator: true, chartHeight: 220 });
    return {
      ...base,
      xAxis: {
        ...base.xAxis,
        events: {
          setExtremes(e) {
            if (e.trigger === "syncExtremes") return; // programmatic sync — no React state update
            if (e.DOMEvent?.type === "mousemove") {
              // Skip React state update during live drag (would call chart.update() and fight the drag).
              // Still sync the demand chart imperatively so both charts move together.
              if (typeof e.min === "number" && typeof e.max === "number") {
                const other = demandRef.current?.chart;
                if (other?.xAxis[0]) other.xAxis[0].setExtremes(e.min, e.max, true, false, { trigger: "syncExtremes" });
              }
              return;
            }
            const next = typeof e.min === "number" && typeof e.max === "number" ? [e.min, e.max] : null;
            if (!sameDomain(xDomain, next)) setXDomain(next);
            if (e.trigger !== "syncExtremes") {
              const other = demandRef.current?.chart;
              if (other?.xAxis[0]) other.xAxis[0].setExtremes(e.min, e.max, true, false, { trigger: "syncExtremes" });
            }
          },
        },
      },
      yAxis: [{
        title: { text: `Energy Cost (${currency || "$"}/kWh)`, style: { color: axisText, fontSize: "12px" } },
        opposite: false,
        gridLineColor: grid,
        startOnTick: false,
        endOnTick: false,
        labels: {
          style: { color: axisText, fontSize: "11px" },
          formatter: formatCostAxisLabel,
        },
      }],
      series: hcCostSeries,
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bg, axisText, grid, panMode, legendGap, currency, hcCostSeries, colorScheme]);

  // ── Per-chart tooltip divs (created inside chart.container so coords are chart-relative) ──
  const demandTooltipRef = useRef(null);
  const costTooltipRef   = useRef(null);

  // Reads raw values by array index — identical to CSV output, consistent at every zoom level.
  // Grouping stays ON only to give a stable per-pixel snap point (pt.x = first raw timestamp
  // of the bucket); the tooltip value comes from the raw data array, not from pt.y (avg).
  const buildDemandTooltip = useCallback((idx) => {
    if (!timesMs.length || idx < 0 || idx >= timesMs.length) return null;
    const { scaleValue, unitLabel } = unitHelpers;
          const dt = formatProjectDateTime(timesMs[idx]);
    const lines = [];
    demandSeries.forEach(({ key, label, color, values }) => {
      if (hidden.has(key)) return;
      const v = scaleValue(values[Math.min(idx, values.length - 1)]);
      if (!Number.isFinite(v)) return;
      lines.push(`<span style="color:${color}">&#9679;</span> ${label}: <b>${formatEnUsValue(v)}</b> ${unitLabel}`);
    });
    if (socData && !hidden.has(socData.key)) {
      const v = toSocPercent(socData.values[Math.min(idx, socData.values.length - 1)]);
      if (Number.isFinite(v))
        lines.push(`<span style="color:${socData.color}">&#9679;</span> ${socData.label}: <b>${formatEnUsValue(v)}</b> %`);
    }
    if (!lines.length) return null;
    return (
      `<div style="font-family:${fontFamily};font-size:11px;line-height:1.7;color:${tooltipText};` +
      `background:${tooltipBg};border:1px solid ${tooltipBorder};border-radius:4px;padding:5px 8px;` +
      `box-shadow:0 2px 8px rgba(0,0,0,.3);white-space:nowrap">` +
      `<strong style="font-size:12px">${dt}</strong><br/>${lines.join("<br/>")}</div>`
    );
  }, [demandSeries, socData, timesMs, hidden, unitHelpers, tooltipBg, tooltipBorder, tooltipText]);

  const buildCostTooltip = useCallback((idx) => {
    if (!timesMs.length || idx < 0 || idx >= timesMs.length) return null;
          const dt = formatProjectDateTime(timesMs[idx]);
    const lines = [];
    costSeries.forEach(({ key, label, color, values }) => {
      if (hidden.has(key)) return;
      const v = +values[Math.min(idx, values.length - 1)];
      if (!Number.isFinite(v)) return;
      lines.push(`<span style="color:${color}">&#9679;</span> ${label}: <b>${formatEnUsValue(v)}</b> ${currency || "$"}/kWh`);
    });
    if (!lines.length) return null;
    return (
      `<div style="font-family:${fontFamily};font-size:11px;line-height:1.7;color:${tooltipText};` +
      `background:${tooltipBg};border:1px solid ${tooltipBorder};border-radius:4px;padding:5px 8px;` +
      `box-shadow:0 2px 8px rgba(0,0,0,.3);white-space:nowrap">` +
      `<strong style="font-size:12px">${dt}</strong><br/>${lines.join("<br/>")}</div>`
    );
  }, [costSeries, timesMs, hidden, tooltipBg, tooltipBorder, tooltipText, currency]);

  // Intentionally no per-point hover state updates.
  // On large datasets this is costly and causes interaction lag.
  const applyHoverPoints = () => {};

  const clearHoverPoints = () => {};

  // Cache tooltip Y once per chart render so it stays stable across mousemoves
  // Create (or reuse) a tooltip div as a direct child of chart.container.
  // Using position:absolute inside a position:relative container means
  // left/top use chart-relative pixels — identical to resultSeries.js positioner.
  const createChartTooltipDiv = (chart, ref) => {
    if (ref.current?.parentNode === chart.container) return; // already attached
    ref.current?.remove();
    const div = document.createElement("div");
    div.style.cssText = "display:none;position:absolute;z-index:9999;pointer-events:none;";
    chart.container.style.position = "relative";
    chart.container.appendChild(div);
    ref.current = div;
  };

  const refreshTooltipDivs = () => {
    const dc = demandRef.current?.chart;
    const cc = costRef.current?.chart;
    if (dc) createChartTooltipDiv(dc, demandTooltipRef);
    if (cc) createChartTooltipDiv(cc, costTooltipRef);
  };

  // Separate callbacks used as HighchartsReact `callback` prop;
  // the chart instance is passed as argument when the chart is first created.
  const onDemandChartReady = useCallback((chart) => {
    if (chart?.container) {
      createChartTooltipDiv(chart, demandTooltipRef);
      applyHiddenVisibility(hiddenRef.current, chart);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onCostChartReady = useCallback((chart) => {
    if (chart?.container) {
      createChartTooltipDiv(chart, costTooltipRef);
      applyHiddenVisibility(hiddenRef.current, chart);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Position tooltip in chart-relative coordinates — no viewport math needed.
  // Mirrors resultSeries.js positioner(labelWidth, _labelHeight, point) exactly.
  const positionTooltip = (chart, tipRef, timestampMs, html) => {
    const tip = tipRef.current;
    if (!tip) return;
    if (!html) { tip.style.display = "none"; return; }
    tip.innerHTML     = html;
    tip.style.display = "block";
    tip.style.left    = "-9999px"; // measure without flash
    const tipW      = tip.offsetWidth || 280;
    const OFFSET    = 14;
    const px        = chart.xAxis[0].toPixels(timestampMs);
    const plotRight = chart.plotLeft + chart.plotWidth;
    const x = px + OFFSET + tipW > plotRight
      ? px - tipW - OFFSET
      : px + OFFSET;
    tip.style.left = `${Math.max(0, x)}px`;
    tip.style.top  = `${chart.plotTop + 8}px`;
  };

  // Draw crosshair + tooltip. Snaps to the nearest rendered (grouped) point's pt.x,
  // which Highcharts sets to the FIRST raw timestamp of the group — then reads the
  // raw value at that index. This matches CSV output exactly at every zoom level.
  const showSyncCrosshairAndTooltips = useCallback((timestampMs) => {
    const demandChart = demandRef.current?.chart;
    const costChart   = costRef.current?.chart;
    if (!demandChart && !costChart) return;

    // Snap x to the nearest rendered series point's x.
    // pt.x = first raw timestamp of the group — a genuine timesMs[] value.
    const chartForSnap = demandChart || costChart;
    let snappedX = timestampMs;
    for (const s of (chartForSnap.series || [])) {
      if (!s.visible || !s.points?.length) continue;
      if (!ALL_SERIES_IDS.has(s.options?.id)) continue;
      const pt = nearestRenderedPoint(s.points, timestampMs);
      if (pt) snappedX = pt.x;
      break;
    }

    const idx = nearestIndex(timesMs, snappedX);
    if (idx < 0) return;
    const displayX = timesMs[idx];

    [demandChart, costChart].filter(Boolean).forEach((c) => {
      if (!c.xAxis[0]) return;
      const px = c.xAxis[0].toPixels(displayX);
      c.xAxis[0].drawCrosshair({ chartX: px, chartY: c.plotTop + 1 });
    });
    if (demandChart) positionTooltip(demandChart, demandTooltipRef, displayX, buildDemandTooltip(idx));
    if (costChart)   positionTooltip(costChart,   costTooltipRef,   displayX, buildCostTooltip(idx));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buildDemandTooltip, buildCostTooltip, timesMs, demandSeries]);

  const scheduleSyncHover = useCallback((timestampMs) => {
    pendingHoverXRef.current = timestampMs;
    if (hoverRafRef.current != null) return;
    hoverRafRef.current = requestAnimationFrame(() => {
      hoverRafRef.current = null;
      const x = pendingHoverXRef.current;
      if (!Number.isFinite(x)) return;
      showSyncCrosshairAndTooltips(x);
    });
  }, [showSyncCrosshairAndTooltips]);

  const hideSyncCrosshairAndTooltips = useCallback(() => {
    [demandRef.current?.chart, costRef.current?.chart].filter(Boolean).forEach((c) => {
      if (c.xAxis[0]) c.xAxis[0].hideCrosshair();
      clearHoverPoints(c);
    });
    if (demandTooltipRef.current) demandTooltipRef.current.style.display = "none";
    if (costTooltipRef.current)   costTooltipRef.current.style.display   = "none";
  }, []);

  // Determine source chart from which container the event originated
  const demandContainerRef = useRef(null);
  const costContainerRef   = useRef(null);

  const syncMoveHandler = useCallback((e) => {
    const isDemand    = demandContainerRef.current?.contains(e.currentTarget);
    const sourceChart = isDemand ? demandRef.current?.chart : costRef.current?.chart;
    if (!sourceChart?.pointer) return;
    // Don't interfere with Highcharts' own navigator drag handling
    const nav = sourceChart.navigator;
    if (nav && (nav.grabbedLeft || nav.grabbedRight || nav.grabbedCenter)) return;
    if (sourceChart.pointer.isDragging) return;
    const evt = sourceChart.pointer.normalize(e);
    const chartX = evt.chartX;

    // Pass raw axis value — snapping to nearest rendered point happens in showSyncCrosshairAndTooltips.
    const rawX = sourceChart.xAxis[0].toValue(chartX);
    if (!Number.isFinite(rawX)) return;
    scheduleSyncHover(rawX);
  }, [scheduleSyncHover]);

  // Attach pointer events
  useEffect(() => {
    const containers = [demandContainerRef.current, costContainerRef.current].filter(Boolean);
    containers.forEach((el) => {
      el.addEventListener("mousemove",  syncMoveHandler);
      el.addEventListener("mouseleave", hideSyncCrosshairAndTooltips);
      el.addEventListener("touchmove",  syncMoveHandler, { passive: true });
    });
    return () => {
      containers.forEach((el) => {
        el.removeEventListener("mousemove",  syncMoveHandler);
        el.removeEventListener("mouseleave", hideSyncCrosshairAndTooltips);
        el.removeEventListener("touchmove",  syncMoveHandler);
      });
    };
  }, [syncMoveHandler, hideSyncCrosshairAndTooltips, maxLen]);

  useEffect(() => {
    return () => {
      if (hoverRafRef.current != null) cancelAnimationFrame(hoverRafRef.current);
    };
  }, []);

  // Fix: navigator drag stays stuck when mouse is released outside the browser window.
  // Only resets when a stale grab is actually detected — avoids corrupting normal pointer state.
  useEffect(() => {
    const releaseNavigator = (e) => {
      if (e.type === "mousemove") {
        if (e.buttons !== 0) return; // button held — live drag, leave alone
        // Button not held: only act if navigator is stale-grabbed
        const hasStale = [demandRef.current?.chart, costRef.current?.chart]
          .filter(Boolean)
          .some((c) => {
            const nav = c.navigator;
            return (nav && (nav.grabbedLeft || nav.grabbedRight || nav.grabbedCenter))
              || !!c.pointer?.isDragging;
          });
        if (!hasStale) return;
      }
      [demandRef.current?.chart, costRef.current?.chart].filter(Boolean).forEach((chart) => {
        try { chart.pointer?.onDocumentMouseUp(e); } catch (_) {}
        try {
          const nav = chart.navigator;
          if (nav) {
            nav.grabbedLeft   = false;
            nav.grabbedRight  = false;
            nav.grabbedCenter = false;
            nav.hasDragged    = false;
          }
        } catch (_) {}
        try {
          if (chart.pointer) {
            chart.pointer.isDragging      = false;
            chart.pointer.hasDragged      = false;
            chart.pointer.selectionMarker = null;
          }
        } catch (_) {}
      });
    };
    document.addEventListener("mouseup",   releaseNavigator);
    document.addEventListener("mousemove", releaseNavigator);
    return () => {
      document.removeEventListener("mouseup",   releaseNavigator);
      document.removeEventListener("mousemove", releaseNavigator);
    };
  }, []);

  // ── Zoom / pan controls ────────────────────────────────────────────────────
  const setExtremesBoth = useCallback((min, max) => {
    [demandRef.current?.chart, costRef.current?.chart].filter(Boolean).forEach((c) => {
      c.xAxis[0].setExtremes(min, max, true, false);
    });
  }, []);

  const handleZoomIn = () => {
    if (!timesMs.length) return;
    const fs = timesMs[0], fe = timesMs[timesMs.length - 1];
    const cs = xDomain ? xDomain[0] : fs, ce = xDomain ? xDomain[1] : fe;
    const mid = (cs + ce) / 2, newSpan = Math.max(stepMs * 4, (ce - cs) * 0.5);
    const ns  = Math.max(fs, mid - newSpan / 2), ne = Math.min(fe, mid + newSpan / 2);
    setExtremesBoth(ns, ne);
  };
  const handleZoomOut = () => {
    if (!timesMs.length || !xDomain) return;
    const fs = timesMs[0], fe = timesMs[timesMs.length - 1];
    const mid = (xDomain[0] + xDomain[1]) / 2, nextSpan = (xDomain[1] - xDomain[0]) / 0.7;
    if (nextSpan >= fe - fs) { setExtremesBoth(null, null); return; }
    setExtremesBoth(mid - nextSpan / 2, mid + nextSpan / 2);
  };
  const handleReset = () => {
    setExtremesBoth(null, null);
    setXDomain(null);
    setHidden(new Set());
  };

  // ── CSV download ──────────────────────────────────────────────────────────
  const handleDownloadCsv = () => {
    try {
      if (!timesMs.length) return;
      const { unitLabel, scaleValue } = unitHelpers;
      const BOM = "\uFEFF";

      const getResultsArrayInsensitive = (obj, key) => {
        if (!obj || !key) return null;
        if (Array.isArray(obj[key])) return obj[key];
        const lower = String(key).toLowerCase();
        const matchKey = Object.keys(obj).find((k) => String(k).toLowerCase() === lower);
        return matchKey && Array.isArray(obj[matchKey]) ? obj[matchKey] : null;
      };

      const csvEscape = (val) => {
        const s = String(val ?? "");
        if (/[,"\n\r]/.test(s) || /[^\x20-\x7E]/.test(s)) {
          return '"' + s.replace(/"/g, '""') + '"';
        }
        return s;
      };

      const csvOnlyCols = [
        {
          key: "purchaseCost",
          header: `Import Tariff (${currency || "$"}/kWh)`,
          values: getResultsArrayInsensitive(results, "purchaseCost"),
        },
        {
          key: "sellbackCost",
          header: `Export Tariff (${currency || "$"}/kWh)`,
          values: getResultsArrayInsensitive(results, "sellbackCost"),
        },
        {
          key: "penalty",
          header: `Penalty (${currency || "$"}/kWh)`,
          values: getResultsArrayInsensitive(results, "penalty"),
        },
        {
          key: "gridImport",
          header: `Grid Import (${unitLabel})`,
          values: getResultsArrayInsensitive(results, "gridImport"),
        },
        {
          key: "gridExport",
          header: `Grid Export (${unitLabel})`,
          values: getResultsArrayInsensitive(results, "gridExport"),
        },
        {
          key: "gridExcess",
          header: `Grid Excess (${unitLabel})`,
          values: getResultsArrayInsensitive(results, "gridExcess"),
        },
        {
          key: "gridUnmet",
          header: `Grid Unmet (${unitLabel})`,
          values: getResultsArrayInsensitive(results, "gridUnmet"),
        },
        {
          key: "batteryCharge",
          header: `Battery Charge (${unitLabel})`,
          values: getResultsArrayInsensitive(results, "batteryCharge"),
        },
        {
          key: "batteryDischarge",
          header: `Battery Discharge (${unitLabel})`,
          values: getResultsArrayInsensitive(results, "batteryDischarge"),
        },
        {
          key: "batterySOC",
          header: "Battery SOC (0-1)",
          values: getResultsArrayInsensitive(results, "batterySOC"),
        },
        {
          key: "tariffEffective",
          header: `Tariff Effective (${currency || "$"}/kWh)`,
          values: getResultsArrayInsensitive(results, "tariffEffective"),
        },
        {
          key: "tariffSubscribed",
          header: `Tariff Subscribed (${currency || "$"}/kWh)`,
          values: getResultsArrayInsensitive(results, "tariffSubscribed"),
        },
      ].filter((col) => Array.isArray(col.values) && col.values.length);

      // Header row
      const headers = [
        "DateTime",
        ...demandSeries.map((s) => `${s.label} (${unitLabel})`),
        ...csvOnlyCols.map((col) => col.header),
      ];
      const rows = [headers.map(csvEscape).join(",")];
      for (let i = 0; i < timesMs.length; i++) {
        const dateStr = moment(timesMs[i]).format("YYYY-MM-DD HH:mm:ss");
        const demandVals = demandSeries.map((s) => {
          const v = scaleValue(s.values[i]);
          return v !== undefined && v !== null ? v : "";
        });
        const csvOnlyVals = csvOnlyCols.map((col) => {
          const v = col.values?.[i];
          return v !== undefined && v !== null ? v : "";
        });
        rows.push([dateStr, ...demandVals, ...csvOnlyVals].map(csvEscape).join(","));
      }
      const blob = new Blob([BOM + rows.join("\n")], { type: "text/csv;charset=utf-8;" });
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement("a");
      a.href     = url;
      a.download = `result-sync-${new Date().toISOString().replace(/[:.]/g, "-")}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (_) {}
  };

  // ── PNG download ───────────────────────────────────────────────────────────
  const handleDownloadPng = () => {
    const charts = [demandRef.current?.chart, costRef.current?.chart].filter(Boolean);
    if (!charts.length) return;
    // Serialize each chart SVG and stack them vertically on one canvas
    const svgStrings = charts.map((c) => {
      const svgEl = c.container?.querySelector("svg");
      if (!svgEl) return null;
      const cl = svgEl.cloneNode(true);
      const bg = document.createElementNS("http://www.w3.org/2000/svg", "rect");
      bg.setAttribute("x", "0"); bg.setAttribute("y", "0");
      bg.setAttribute("width",  svgEl.getAttribute("width")  || c.chartWidth  || 1000);
      bg.setAttribute("height", svgEl.getAttribute("height") || c.chartHeight || 300);
      bg.setAttribute("fill", colorScheme === "dark" ? "#111111" : "#ffffff");
      cl.insertBefore(bg, cl.firstChild);
      return { svg: new XMLSerializer().serializeToString(cl), w: c.chartWidth || 1000, h: c.chartHeight || 300 };
    }).filter(Boolean);

    const totalW = Math.max(...svgStrings.map((s) => s.w));
    const totalH = svgStrings.reduce((sum, s) => sum + s.h, 0);
    const canvas = document.createElement("canvas");
    canvas.width = totalW; canvas.height = totalH;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    let drawn = 0, offsetY = 0;
    svgStrings.forEach(({ svg, h }) => {
      const img = new Image();
      img.onload = () => {
        ctx.drawImage(img, 0, offsetY);
        offsetY += h;
        drawn++;
        if (drawn === svgStrings.length) {
          canvas.toBlob((blob) => {
            if (!blob) return;
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `result-sync-${new Date().toISOString().replace(/[:.]/g, "-")}.png`;
            document.body.appendChild(a); a.click(); a.remove();
            URL.revokeObjectURL(url);
          });
        }
      };
      img.src = "data:image/svg+xml;charset=utf-8," + encodeURIComponent(svg);
    });
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  const hasDemandData = demandSeries.length > 0;
  const hasCostData   = costSeries.length > 0;

  return (
    <div className={`rs-wrapper ${colorScheme === "dark" ? "dark" : "light"}`} style={{ position: "relative" }}>
      {/* Toolbar */}
      <div className="rs-toolbar">
        <button
          className="rs-btn"
          aria-label={inModalInstance ? "time-series-close-fullscreen" : "time-series-open-fullscreen"}
          title={inModalInstance ? t("common.closeFullscreen") : t("common.openFullscreen")}
          onClick={() => inModalInstance ? handleCloseModal() : setFullscreenModal(true)}
        >
          <span role="img" aria-hidden="true" className="rs-pan-icon">{inModalInstance ? "🗗" : "⛶"}</span>
        </button>
        {/* <button
          className={`rs-btn rs-btn-pan ${panMode ? "is-active" : ""}`}
          aria-label={panMode ? "time-series-disable-pan" : "time-series-enable-pan"}
          title={panMode ? "Pan enabled (click to disable)" : "Pan disabled (click to enable)"}
          aria-pressed={panMode}
          onClick={() => setPanMode((p) => !p)}
        >
          <span role="img" aria-hidden="true" className="rs-pan-icon">{panMode ? "🖐️" : "↔"}</span>
        </button> */}
        <button className="rs-btn" aria-label="zoom-in"    title={t("common.zoomIn")}    onClick={handleZoomIn}>+</button>
        <button className="rs-btn" aria-label="zoom-out"   title={t("common.zoomOut")}   onClick={handleZoomOut}>-</button>
        <button className="rs-btn" aria-label="reset-zoom" title={t("common.resetZoom")} onClick={handleReset}>Reset</button>
        <select
          className="rs-btn rs-download-select"
          value={powerUnit}
          title={t("timeSeries.unitToggle")}
          onChange={(e) => setPowerUnit(e.target.value)}
          style={{ width: 90 }}
        >
          <option value="W">W</option>
          <option value="kW">kW</option>
        </select>
        {/* <button className="rs-btn" title={t("common.download") || "Download PNG"} onClick={handleDownloadPng}>PNG</button> */}
        <select
          className="rs-btn rs-download-select"
          defaultValue=""
          title={t("common.download") || "Download"}
          onChange={(e) => {
            const v = e.target.value;
            e.target.value = "";
            if (v === "png") handleDownloadPng();
            else if (v === "csv") handleDownloadCsv();
          }}
          style={{ width: 110 }}
        >
          <option value="" disabled hidden>{t("common.download") || "Download"}</option>
          <option value="png">PNG</option>
          <option value="csv">CSV</option>
        </select>
      </div>

      {/* Legend */}
      <div className="rs-legend">
        {legendItems.map((s) => {
          const isHidden = hidden.has(s.key);
          return (
          <button
            key={s.key}
            type="button"
            style={{
              margin: "2px 10px", padding: "4px 10px",
              opacity: isHidden ? "0.45" : "1",
              background: isHidden
                ? (colorScheme === "dark" ? "#1e1e1e" : "#f1f3f5")
                : (colorScheme === "dark" ? "#2e2e2e" : "#ffffff"),
            }}
            title={isHidden ? `Show ${s.label}` : `Hide ${s.label}`}
            onClick={() => handleLegendToggle(s.key)}
          >
            <span style={{ width: 14, height: 8, borderRadius: 2, background: s.color, display: "inline-block", marginRight: 6 }} />
            <span style={{ fontSize: 11, color: colorScheme === "dark" ? "#d5d7e0" : "#222" }}>
              {`${s.label}: Min ${s.minText} ${s.unitLabel} , Max ${s.maxText} ${s.unitLabel}`}
            </span>
          </button>
          );
        })}
      </div>

      {/* Demand chart */}
      {hasDemandData ? (
        <div ref={demandContainerRef}>
          <HighchartsReact
            highcharts={Highcharts}
            constructorType="stockChart"
            options={demandOptions}
            ref={demandRef}
            callback={onDemandChartReady}
            containerProps={{ className: "indicator-downloadable rs-chart" }}
          />
        </div>
      ) : (
        <div className="rs-chart" style={{ minHeight: 120, padding: "12px 16px", color: axisText }}>
          {t("common.noDataAvailable")}
        </div>
      )}

      {/* Divider label */}
      {hasCostData && (
        <div style={{ padding: "4px 12px", fontSize: 11, fontWeight: 600, color: axisText, fontFamily }}>
          Energy Cost ({currency || "$"}/kWh)
        </div>
      )}

      {/* Cost chart */}
      {hasCostData ? (
        <div ref={costContainerRef}>
          <HighchartsReact
            highcharts={Highcharts}
            constructorType="stockChart"
            options={costOptions}
            ref={costRef}
            callback={onCostChartReady}
            containerProps={{ className: "indicator-downloadable rs-chart" }}
          />
        </div>
      ) : (
        <div className="rs-chart" style={{ minHeight: 80, padding: "8px 12px", fontSize: 12, color: axisText }}>
          {t("common.noDataAvailable")} — Energy Charges / Demand Charges
        </div>
      )}

      <FullscreenModal
        open={fullscreenModal && !inModalInstance}
        onClose={handleCloseModal}
        title={t("result.timeSeries.title")}
      >
        <ResultSeriesSync
          results={results}
          onFullscreen={handleCloseModal}
          inModalInstance
          kpiProjectSettings={kpiProjectSettings}
          thermalMode={thermalMode}
          co2Mode={co2Mode}
        />
      </FullscreenModal>
    </div>
  );
};

export default ResultSeriesSync;
